import type { ModuleKey } from '@titularis/ui';
import type { FiltrosDeEstudo } from './components/PainelDeFiltros';

/**
 * O recorte que chega de fora da gaveta de filtros, sempre por link.
 *
 * Ninguém o escolhe na tela de estudo: ele vem da etiqueta da Legislação
 * Comentada (lei e artigo) ou de um link que aponta questões pelo id - o do
 * cronograma de estudos, por exemplo - e sai quando o aluno pede "Ver todas".
 */
export interface RecorteDoLink {
  leiCitada?: string;
  artigoCitado?: string;
  /** Questões escolhidas pelo id, na ordem em que a lista deve sair. */
  questoes: string[];
}

/**
 * O que a tela de estudo manda para a API.
 *
 * Mora fora da tela porque é regra, e não desenho: ela decide que filtro vale
 * para qual módulo, e um valor herdado de outro módulo que passasse por aqui
 * viraria uma lista vazia sem explicação.
 *
 * - Só entra o que vale para o módulo: o ENAC não tem banca nem estado, a
 *   discursiva não tem classificação, e o tempo do ENAC é a edição (2025.1),
 *   não o ano.
 * - Lei e artigo vão juntos ou nenhum vai: `art. 108` sem dizer de que lei é
 *   não identifica nada, e a API devolveria a lista inteira como se filtrasse.
 * - As questões do link vão como `questionIds`, e casam com o resto por E.
 */
export function filtrosDaConsulta(
  module: ModuleKey,
  aplicados: FiltrosDeEstudo,
  recorte: RecorteDoLink,
): Record<string, unknown> {
  const enac = module === 'enac';
  const essay = module === 'essay';
  const {
    years,
    editions,
    boardIds,
    notaryOfficeIds,
    disciplineId,
    subjectIds,
    subjectDetailIds,
    essayKinds,
  } = aplicados;

  const filtros: Record<string, unknown> = { module };
  if (enac) {
    if (editions?.length) filtros.editions = editions;
  } else if (years?.length) {
    filtros.years = years;
  }
  if (!enac) {
    if (boardIds?.length) filtros.boardIds = boardIds;
    if (notaryOfficeIds?.length) filtros.notaryOfficeIds = notaryOfficeIds;
  }
  if (essay) {
    // Só a 2ª fase tem tipo (dissertação, peça prática, questão).
    if (essayKinds?.length) filtros.essayKinds = essayKinds;
  } else {
    if (disciplineId) filtros.disciplineId = disciplineId;
    if (subjectIds?.length) filtros.subjectIds = subjectIds;
    if (subjectDetailIds?.length) filtros.subjectDetailIds = subjectDetailIds;
  }

  if (recorte.leiCitada && recorte.artigoCitado) {
    filtros.legislation = recorte.leiCitada;
    filtros.artigo = recorte.artigoCitado;
  }

  if (recorte.questoes.length > 0) filtros.questionIds = recorte.questoes;

  return filtros;
}
